// Generated by Copilot- Start
/**
 * ============================================================================
 * MODALS.TS - Modal flags, data, MyFree DTV, and theme resolution
 * ============================================================================
 *
 * This file consolidates all modal-related operations:
 * - Modal open/close flags based on variant type
 * - RSN modal data preparation
 * - MyFree DTV object building
 * - Theme resolution
 *
 * Replaces: modal-flags.ts, modal-data.ts, myfree-helper.ts, theme-helper.ts
 */

import { ChannelCardV2, RsnModalPayload } from "./types";
import { defaultThemingDark, parseThemeObject } from "../channel-interactive-tool-theme";
import { cleanEmpty, arraytoObj } from "../sb-fixer";
import { logError } from "../logger";

// ============================================================================
// SECTION 1: MODAL FLAGS
// ============================================================================

interface ModalFlagsConfig {
  singlePackageView?: boolean;
  singlePackageConfig?: {
    openChannelModal?: boolean;
    openSvodModal?: boolean;
  };
  showMyFreeDTVChannels?: boolean;
  displayGenrePack?: boolean;
}

/**
 * Determines if channel modal should be opened when a channel is clicked
 *
 * Logic:
 * 1. If singlePackageView = true, use singlePackageConfig.openChannelModal (defaults to false)
 * 2. If showMyFreeDTVChannels = true (MyFree variant), return false (no modal for MyFree)
 * 3. If displayGenrePack = true (genre variant), return false (no modal in genre mode)
 * 4. Otherwise, return true (default: open modal)
 */
export const handleOpenChannelModal = (config: ModalFlagsConfig): boolean => {
  // Priority 1: Single package variant
  if (config.singlePackageView === true) {
    // Use explicit flag, default to false for single package view
    return config.singlePackageConfig?.openChannelModal === true;
  }

  // Priority 2: MyFree DTV variant - no modals
  if (config.showMyFreeDTVChannels === true) {
    return false;
  }

  // Priority 3: Genre variant - no modals
  if (config.displayGenrePack === true) {
    return false;
  }

  // Default: open channel modal
  return true;
};

/**
 * Determines if SVOD modal should be opened when an SVOD tile is clicked
 *
 * Logic:
 * 1. If singlePackageView = true, use singlePackageConfig.openSvodModal (defaults to false)
 * 2. If showMyFreeDTVChannels = true (MyFree variant), return false (no modal for MyFree)
 * 3. If displayGenrePack = true (genre variant), return false (no modal in genre mode)
 * 4. Otherwise, return true (default: open modal)
 */
export const handleOpenSvodModal = (config: ModalFlagsConfig): boolean => {
  // Priority 1: Single package variant
  if (config.singlePackageView === true) {
    // Use explicit flag, default to false for single package view
    return config.singlePackageConfig?.openSvodModal === true;
  }

  // Priority 2: MyFree DTV variant - no modals
  if (config.showMyFreeDTVChannels === true) {
    return false;
  }

  // Priority 3: Genre variant - no modals
  if (config.displayGenrePack === true) {
    return false;
  }

  // Default: open SVOD modal
  return true;
};

// ============================================================================
// SECTION 2: RSN MODAL DATA
// ============================================================================

export const buildRsnModalData = (
  rsnChannels: ChannelCardV2[] = []
): Record<string, RsnModalPayload> => rsnChannels.reduce((acc, channel) => {
  if (!channel?.id) {
    return acc;
  }

  acc[channel.id] = {
    channelId: channel.id,
    legal: undefined, // Legal text can be derived from package summaries if needed
    dmaData: undefined,
    ctas: undefined
  };

  return acc;
}, {} as Record<string, RsnModalPayload>);

// ============================================================================
// SECTION 3: MYFREE DTV
// ============================================================================

/**
 * Builds the MyFree DTV object from CMS data
 * Matches V1 adapter logic exactly:
 * - Spreads content.myFreeDTVObj[0]
 * - Replaces [chnlcnt] placeholder in banner with actual channel count
 * - Injects CTA from packageConfig
 * - Returns normalized object structure
 *
 * @param myFreeDTVObjArray - Raw MyFree DTV array from CMS (content.myFreeDTVObj)
 * @param packageConfig - Package configuration array to get MYFREE_DTV details
 * @returns MyFree DTV object with enriched data
 */
export const buildMyFreeDtvObject = (
  myFreeDTVObjArray: any[],
  packageConfig: any[]
): any => {
  if (!myFreeDTVObjArray || !Array.isArray(myFreeDTVObjArray) || myFreeDTVObjArray.length === 0) {
    return {};
  }

  // Get MYFREE_DTV package details
  const myfreeDataPkg = packageConfig?.find(pkg => pkg.id === "MYFREE_DTV");
  const myfreeDtvChnlCnt = myfreeDataPkg?.channel?.count;
  const myfreeDtvCta = myfreeDataPkg?.cta;

  // Get banner text and replace placeholder
  const bannerArray = myFreeDTVObjArray[0]?.banner;
  let bannerContent = "";

  if (Array.isArray(bannerArray) && bannerArray.length > 0) {
    bannerContent = bannerArray[0]?.content?.replace('[chnlcnt]', myfreeDtvChnlCnt) || "";
  } else if (bannerArray && typeof bannerArray === 'object') {
    bannerContent = bannerArray.content?.replace('[chnlcnt]', myfreeDtvChnlCnt) || "";
  }

  return {
    ...myFreeDTVObjArray[0],
    cta: myfreeDtvCta,
    banner: {
      content: bannerContent
    }
  };
};

// ============================================================================
// SECTION 4: THEME RESOLUTION
// ============================================================================

/**
 * Resolves theme configuration from CMS content
 * - Handles both array and object theme structures
 * - Applies cleanEmpty(arraytoObj()) for Storyblok normalization
 * - Falls back to defaultThemingDark if no valid theme found
 *
 * @param theme - Raw theme data from CMS content
 * @param adapterName - Name of adapter for error logging
 * @param index - Component index for error logging
 * @returns Resolved theme object
 */
export const resolveTheme = (
  theme: any,
  adapterName: string,
  index?: number
): any => {
  // Default theme from shared helper
  let resolvedTheme = parseThemeObject(defaultThemingDark);

  // Handle array format (Storyblok bloks)
  const themeConfig = Array.isArray(theme) && theme.length > 0 ?
    theme[0] :
    theme || null;

  if (themeConfig && typeof themeConfig === "object") {
    try {
      // Check if it's a normalized object with pageBackgroundColor
      if (themeConfig?.pageBackgroundColor) {
        const normalizedTheme = cleanEmpty(arraytoObj(themeConfig)) || {};
        resolvedTheme = Object.keys(normalizedTheme).length ?
          normalizedTheme :
          resolvedTheme;
      // Check if it needs parsing with page property
      } else if (themeConfig?.page) {
        resolvedTheme = parseThemeObject(themeConfig);
      }
    } catch (themeError) {
      logError(adapterName, themeError, {
        step: "resolveTheme",
        index
      });
    }
  }

  return resolvedTheme;
};
// Generated by Copilot- End
